import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { DashboardShell, PlaceholderCard } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { supabase } from "@/integrations/supabase/client";
import { CATEGORY_LABEL, LISTING_CATEGORIES, type ListingCategory } from "@/lib/categories";

export const Route = createFileRoute("/_authenticated/super-admin/category-pricing")({
  head: () => ({
    meta: [
      { title: "Category pricing · Landoo Super Admin" },
      {
        name: "description",
        content: "Separate subscription pricing for Sale, Rent, Lease and Parking categories.",
      },
    ],
  }),
  component: CategoryPricingPage,
});

interface PlanRow {
  id: string;
  category: ListingCategory;
  name: string;
  price_monthly: number;
  price_yearly: number | null;
  listing_limit: number | null;
  active: boolean;
}

function CategoryPricingPage() {
  const { data = [] } = useQuery({
    queryKey: ["category-plans"],
    queryFn: async () => {
      const { data } = await supabase.from("category_plans").select("*").order("price_monthly");
      return (data as unknown as PlanRow[]) ?? [];
    },
  });

  return (
    <DashboardShell title="Category pricing" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <p className="text-sm text-muted-foreground">
        Each business category is charged separately. A partner subscribing to Parking is not billed
        for Sale, Rent or Lease.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {LISTING_CATEGORIES.map((c) => {
          const plans = data.filter((p) => p.category === c.value);
          return (
            <div key={c.value} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="text-sm font-bold text-brown-dark">{CATEGORY_LABEL[c.value]}</div>
              <div className="text-xs text-muted-foreground">{c.blurb}</div>
              <div className="mt-3 space-y-2">
                {plans.length === 0 && (
                  <div className="text-xs text-muted-foreground">No plans configured yet.</div>
                )}
                {plans.map((p) => (
                  <div key={p.id} className="rounded-xl bg-cream/60 px-3 py-2">
                    <div className="text-xs font-semibold text-brown-dark">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      ₹{p.price_monthly}/mo
                      {p.price_yearly ? ` · ₹${p.price_yearly}/yr` : ""} ·{" "}
                      {p.listing_limit == null
                        ? "Unlimited listings"
                        : `${p.listing_limit} listings`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <PlaceholderCard
          title="Plan editor"
          description="Creating and editing category plans from this console is coming soon."
        />
        <PlaceholderCard
          title="Billing engine"
          description="Payment collection and renewals are not enabled yet."
        />
      </div>
    </DashboardShell>
  );
}
